import { distance } from '@util/index';
import type { ProductDocument } from 'models/product';
import type { Bike } from 'models/shared';

export const MX_DIRECT_SELLER_ID = 'E16WuBcn0UMejlp0FSdQulSSed12';

const NON_VEHICLE_SPECIFIC_CATEGORIES = ['Tires', 'Grips'];

type SelectableBike = {
  type?: string | null;
  make?: string | null;
  model?: string | null;
  year?: number | null;
};

export const isMXLockerDirect = (
  product: Pick<ProductDocument, 'seller_id'>
): boolean => product.seller_id === MX_DIRECT_SELLER_ID;

const isExcludedCategory = (category2?: string | null): boolean =>
  NON_VEHICLE_SPECIFIC_CATEGORIES.includes(category2 ?? '');

export const isSelectedBikeComplete = (bike: SelectableBike): boolean =>
  Boolean(bike.type && bike.make && bike.model && bike.year);

export const isVehicleSpecificDirectCandidate = (
  product: ProductDocument
): boolean =>
  isMXLockerDirect(product) &&
  product.integration_info?.type === 'warehouse' &&
  !!product.group_id &&
  !isExcludedCategory(product.category2);

export const isVehicleSpecificDirect = (
  product: ProductDocument,
  relatedItems: ProductDocument[]
): boolean =>
  isVehicleSpecificDirectCandidate(product) &&
  relatedItems.some((item) => !!item.fitting_bikes?.bikes?.length) &&
  !relatedItems.some((item) => isExcludedCategory(item.category2));

export const filterFittingItems = (
  relatedItems: ProductDocument[],
  bike: SelectableBike | null | undefined
): ProductDocument[] =>
  relatedItems.filter((item) => {
    // Non-vehicle-specific categories are shown regardless of the selected vehicle
    if (isExcludedCategory(item.category2)) return true;
    // Variations without fitment data fit every vehicle
    if (!item.fitting_bikes?.bikes?.length) return true;
    // Nothing to filter against until a complete vehicle is selected
    if (!bike?.make || !bike.model || !bike.year) return true;
    // Only keep a variation when it fits the selected vehicle exactly
    return item.fitting_bikes.bikes.some(
      (b) =>
        b.make === bike.make && b.model === bike.model && b.year === bike.year
    );
  });

const preferInStockThenStock = (a: ProductDocument, b: ProductDocument) => {
  const aInStock = a.out_of_stock ? 0 : 1;
  const bInStock = b.out_of_stock ? 0 : 1;
  if (aInStock !== bInStock) return bInStock - aInStock;
  return (b.stock ?? 0) - (a.stock ?? 0);
};

export const findMatchingListing = (
  relatedItems: ProductDocument[],
  bike: SelectableBike
): ProductDocument | null => {
  if (!isSelectedBikeComplete(bike)) return null;
  const { make, model, year } = bike;

  const exact = relatedItems
    .filter((item) =>
      item.fitting_bikes?.bikes?.some(
        (fb) => fb.make === make && fb.year === year && fb.model === model
      )
    )
    .sort(preferInStockThenStock);
  if (exact.length) return exact[0];

  const fuzzy = relatedItems
    .filter((item) =>
      item.fitting_bikes?.bikes?.some(
        (fb) =>
          fb.make === make &&
          fb.year === year &&
          distance(fb.model, model ?? null, { caseSensitive: false }) > 0.8
      )
    )
    .sort(preferInStockThenStock);
  if (fuzzy.length) return fuzzy[0];

  // A universal variation covers every vehicle, so it stands in when no
  // vehicle-specific variation in the group matches the selection.
  const universal = relatedItems
    .filter((item) => item.fitting_bikes?.universal)
    .sort(preferInStockThenStock);
  if (universal.length) return universal[0];

  return null;
};

export const aggregateGroupFitment = (
  relatedItems: ProductDocument[]
): { bikes: Bike[]; allUniversal: boolean } => {
  const seen = new Set<string>();
  const bikes: Bike[] = [];
  const allUniversal =
    relatedItems.length > 0 &&
    relatedItems.every((i) => i.fitting_bikes?.universal === true);
  for (const item of relatedItems) {
    for (const fb of item.fitting_bikes?.bikes ?? []) {
      const key = `${fb.make}|${fb.model ?? ''}|${fb.year ?? ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      bikes.push(fb);
    }
  }

  return { bikes, allUniversal };
};
